import { AfterViewInit, Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as am5exporting from '@amcharts/amcharts5/plugins/exporting';

import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { CommonService } from '../../../../shared/services/common.service';
import { SpinnerService } from '../../../../shared/services/spinner.service';

/**
 * Line Graph Component
 * @author: PRachethan | 02-08-2025 
 */
@Component({
  selector: 'bi-line-graph',
  standalone: false,
  templateUrl: './line-graph.component.html',
  styleUrl: './line-graph.component.scss'
})
export class LineGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root;
  private graphReportSubscription: any;
  data: BarChartData[] = [];
  private isLineViewInitialized = false;

  @Input() reportData: any = null;

  constructor(
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _commonService: CommonService,
    private _spinnerService: SpinnerService
  ) { }

  ngOnInit(): void {
    // Set up subscription first
    this.graphReportSubscription = this._commonService.graphReportState.subscribe((data: any) => {

      if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
        // Transform the data to match the expected format
        this.data = this._baseChartService.transformReportData(data);

        // Run chart creation in the correct zone
        this.zone.runOutsideAngular(() => {
          try {
            if (this.isLineViewInitialized) {
              this.updateChart(this.data);
            }
          } catch (error) {
            console.error('Error creating chart:', error);
          }
        });
      } else {
        console.log('No valid report data found in subscription');
      }
    });

    // We need to run amCharts code outside of Angular's zone to prevent change detection issues
    this.zone.runOutsideAngular(() => {
      if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      }
    });
  }

  ngAfterViewInit(): void {
    this.isLineViewInitialized = true;
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      });
    }
  }

  ngOnChanges(): void {
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        try {
          this.data = this._baseChartService.transformReportData(this.reportData);
          this.updateChart(this.data);
        } catch (error) {
          console.error('Error processing input data:', error);
        }
      });
    }
  }

  /**
    * Create chart
   * @param data - Chart data
   */
  createChart(data: BarChartData[]) {
    const chartContainer = document.getElementById('line-chartdiv');
    if (!chartContainer) {
      console.error('Chart container not found! Make sure element with id "line-chartdiv" exists');
      return;
    }

    try {
      this.root = this._baseChartService.createRoot('line-chartdiv');

      const categoryField = this._baseChartService.getCategoryField(data);
      const valueFields = this._baseChartService.getValueFields(data);

      const chart = this._baseChartService.createChartContainer(this.root, {
        panX: true,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        layout: this.root.verticalLayout
      });

      // Enhanced chart animation
      this._baseChartService.setupChartAnimation(chart, 'slide');

      this._baseChartService.setupColorPalette(chart);

      const categoryAxis = this._baseChartService.createCategoryAxis(chart, categoryField, {
        axisType: 'x',
        minGridDistance: 20,
        cellStartLocation: 0.1,
        cellEndLocation: 0.9,
        rotation: 45
      });

      // Enhanced axis animation
      this._baseChartService.setupAxisAnimation(categoryAxis);

      const valueAxis = this._baseChartService.createValueAxis(chart, {
        axisType: 'y',
      });

      // Enhanced axis animation for value axis
      this._baseChartService.setupAxisAnimation(valueAxis);

      // Add cursor for hover + tooltip
      const cursor = chart.set('cursor', am5xy.XYCursor.new(this.root, {
        xAxis: categoryAxis,
        yAxis: valueAxis
      }));
      cursor.lineY.set('visible', false);

      // Set data
      categoryAxis.data.setAll(data);

      // Create series for each value field
      valueFields.forEach((valueField, index) => {

        let series = chart.series.push(
          am5xy.LineSeries.new(this.root, {
            xAxis: categoryAxis,
            yAxis: valueAxis,
            valueYField: valueField,
            categoryXField: categoryField,
            name: valueField,
            visible: true // Show all series
          })
        );

        // Enhanced series animation
        this._baseChartService.setupSeriesAnimation(series, 'elastic');

        // Add bullets (nodes) to the line series
        series.bullets.push((root, series, dataItem) => {
          const bullet = am5.Bullet.new(root, {
            sprite: am5.Circle.new(root, {
              radius: 6,
              fill: series.get('fill'),
              stroke: root.interfaceColors.get('background'),
              strokeWidth: 2
            })
          });
          
          return bullet;
        });

        // Tooltip background matches the hovered line color
        const tooltip = am5.Tooltip.new(this.root, {
          getFillFromSprite: true,
          labelText: `{name}: {valueY.formatNumber("#,##0")}\n{categoryX}`,
          pointerOrientation: 'horizontal'
        });
        tooltip.label.set('fill', am5.color(0xFFFFFF));
        this._baseChartService.setupTooltipAnimation(tooltip);
        series.set('tooltip', tooltip);

        // Set data
        series.data.setAll(data);

      });

      // Snap cursor to series for stable tooltips
      if (cursor) {
        cursor.set('snapToSeries', chart.series.values);
      }

      // Add legend like legacy implementation
      let legend = chart.children.push(
        this._baseChartService.createLegend(this.root)
      );

      // Enhanced legend animation
      this._baseChartService.setupLegendAnimation(legend);

      // Configure legend markers like legacy implementation
      let markerTemplate = legend.markers.template;
      markerTemplate.set('width', 12);
      markerTemplate.set('height', 12);

      // Set legend data
      legend.data.setAll(chart.series.values);

      // Trigger enhanced chart animation
      setTimeout(() => {
        chart.appear(1200, 100);
      }, 100);

      // Enable export menu (PNG, JPG, SVG, PDF, CSV/JSON)
      this._baseChartService.attachExporting(this.root, { 
        filePrefix: 'line-chart',
        data: data,
      });

    } catch (error) {
      console.error('Error creating line chart:', error);
    }
  }

  /**
   * Update the chart with new data
   * @param data - New data to display
   */
  private updateChart(data: BarChartData[]) {
    // Update the stored data
    this.data = data;
    
    // Dispose existing chart first
    if (this.root) {
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }
    
    // Add a small delay to ensure proper disposal
    setTimeout(() => {
      // Create a new chart with the updated data
      this.createChart(data);
      
      // Update the chart data in the registry
      BaseChartService.updateActiveChartData(data);
    }, 150);
  }

  ngOnDestroy(): void {
    this._baseChartService.disposeChart(this.root);
    if (this.graphReportSubscription) {
      this.graphReportSubscription.unsubscribe();
    }
  }
}
